import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api";
import { Chip, Ic } from "../components/ui";
import { IconTip } from "../components/EtichetaTip";
import Volum from "../components/Volum";
import { useSession } from "../session";
import { numeDisciplina } from "../solutions/shared";
import { numar, ora, data, t as tr, tn, useT } from "../i18n";

// Lista de proiecte — cardurile din machetă (mock-ui), cu datele reale.
//
// Fiecare card arăta înainte două chipsuri scrise în cod, „fără dosar" și
// „nicio soluție", pe toate proiectele deodată: o afirmație care nu se uita
// niciodată la proiect. Acum tot ce scrie pe card vine din `rezumat` — câte
// documente are, ce discipline au dat o soluție, câte verificări au picat, ce
// rulează chiar acum — iar miniatura desenează volumul din regimul de înălțime
// citit în dosar.

export default function Projects() {
  const { user } = useSession();
  const navigate = useNavigate();
  const t = useT();
  const organizations = user?.organizations ?? [];
  // "" = toate organizațiile. Înainte se pornea pe prima din listă, iar
  // proiectele din a doua păreau dispărute până dădeai de selector.
  const [orgId, setOrgId] = useState("");
  const [cauta, setCauta] = useState("");
  const [projects, setProjects] = useState<api.Project[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setProjects(null);
    api.listProjects(orgId || undefined).then(setProjects)
      .catch(() => setError(t("proiecte.esec_incarcare")));
  }, [orgId]);

  const vizibile = useMemo(() => {
    const q = cauta.trim().toLowerCase();
    if (!q) return projects ?? [];
    return (projects ?? []).filter((p) =>
      p.name.toLowerCase().includes(q)
      || (p.rezumat?.adresa ?? "").toLowerCase().includes(q));
  }, [projects, cauta]);

  async function archive(project: api.Project) {
    if (!confirm(t("proiecte.confirma_arhivare", { proiect: project.name }))) return;
    setError("");
    // Refuzul serverului TREBUIE să ajungă pe ecran. Fără `catch`, un 403 se
    // pierdea în consolă, cardul rămânea pe loc, iar butonul arăta pur și
    // simplu mort — exact raportul „arhivarea nu funcționează".
    try {
      await api.archiveProject(project.id);
    } catch (err) {
      const cod = err instanceof api.ApiError ? err.code : "";
      setError(t(cod === "forbidden" ? "proiecte.arhivare_interzisa"
                                     : "proiecte.esec_arhivare",
                 { proiect: project.name }));
      return;
    }
    setProjects((current) => (current ?? []).filter((p) => p.id !== project.id));
  }

  return (
    <div className="page proiecte">
      <div className="page-head">
        <div>
          <div className="disp">{t("proiecte.titlu")}</div>
          {projects !== null && projects.length > 0 && (
            <div className="proiecte-sumar">{sumar(projects)}</div>
          )}
        </div>
        {organizations.length > 1 && (
          <div className="tabs" style={{ fontSize: 12 }}>
            <button className={orgId === "" ? "on" : ""}
                    onClick={() => setOrgId("")}>{t("proiecte.toate")}</button>
            {organizations.map((org) => (
              <button key={org.id} className={orgId === org.id ? "on" : ""}
                      onClick={() => setOrgId(org.id)}>{org.name}</button>
            ))}
          </div>
        )}
        <div className="sp" />
        {(projects?.length ?? 0) > 4 && (
          <label className="inp proiecte-cauta">
            <Ic n="search" s={14} />
            <input placeholder={t("proiecte.cauta")} value={cauta}
                   onChange={(e) => setCauta(e.target.value)} />
          </label>
        )}
        {/* Proiectul nou începe cu ALEGEREA tipului, pe pagina ei: tipul
            hotărăște ce cere dosarul și ce se generează, deci se alege
            înainte de nume. Organizația filtrată aici vine cu ea. */}
        <button className="btn btnp"
                onClick={() => navigate(`/projects/new${orgId ? `?org=${orgId}` : ""}`)}>
          <Ic n="plus" s={14} />{t("proiecte.nou")}
        </button>
      </div>

      {error && <p className="note bad">{error}</p>}
      {projects === null && <p className="note">{t("comun.se_incarca")}</p>}

      {projects?.length === 0 && (
        <div className="card" style={{ padding: "22px 24px", maxWidth: 560 }}>
          <b>{t("proiecte.gol_titlu")}</b>
          <p className="note" style={{ marginTop: 6 }}>{t("proiecte.gol_text")}</p>
        </div>
      )}
      {projects !== null && projects.length > 0 && vizibile.length === 0 && (
        <p className="note">
          {t("proiecte.fara_potrivire", { cautare: cauta.trim() })}
        </p>
      )}

      <ul className="cards">
        {vizibile.map((project) => (
          <li key={project.id} className="card project-card clicabil"
              onClick={() => navigate(deschide(project))}>
            <Volum cheie={project.id} niveluri={project.rezumat?.niveluri ?? null}
                   silueta={project.rezumat?.silueta} tip={project.tip} />
            {/* Tipul, pe miniatură: lista amestecă proiecte care pornesc de la
                teren cu proiecte care pornesc de la o arhitectură, iar pașii
                și dosarul lor diferă. */}
            <span className="project-tip"><IconTip tip={project.tip} s={13} />
              {t(project.tip === "structura" ? "tip.structura_scurt" : "tip.complet_scurt")}</span>
            {/* Butonul apare doar cui i-ar și merge: arhivarea cere drept de
                scriere, iar un buton care răspunde cu 403 e mai rău decât
                unul care lipsește. `catch`-ul din `archive` rămâne ca plasă —
                rolul se poate schimba între încărcarea listei și click.
                `stopPropagation`: cardul întreg e clicabil, iar fără asta
                arhivarea ar deschide și proiectul pe care tocmai l-ai scos
                din listă. */}
            {(project.role === "editor" || project.role === "admin") && (
              <button className="project-arh" title={t("proiecte.arhiveaza")}
                      onClick={(e) => { e.stopPropagation(); archive(project); }}>
                <Ic n="x" s={13} />
              </button>
            )}
            <div className="project-corp">
              <span className="name">{project.name}</span>
              <span className="meta">{descriere(project)}</span>
              <div className="project-chipuri">{chipuri(project)}</div>
              <div className="lbl project-subsol">{subsol(project)}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Unde duce un click pe card.
 *
 *  Cine deschide un proiect vrea să vadă PROIECTUL, nu dosarul din care a
 *  ieșit: pe vizualizator, unde se deschide ultima soluție privită, altfel
 *  firul implicit (soluția implicită a fiecărei etape, aleasă de server).
 *
 *  `rezumat.discipline` numără doar soluțiile `ready` (v.
 *  `api/projects.py::_rezumate`), deci o rulare picată sau una încă în lucru
 *  nu fură deschiderea.
 *
 *  Dosarul rămâne destinația unui proiect NOU, care încă n-are nicio soluție:
 *  acolo chiar urmează să urci actele. */
function deschide(project: api.Project): string {
  // `/p/:id` se rezolvă singur: ultima rută privită pe aparatul ăsta, altfel
  // firul implicit al proiectului (contract §8) — v. `App.StartProiect`.
  const are = (project.rezumat?.discipline ?? []).length > 0;
  return are ? `/p/${project.id}` : `/p/${project.id}/dossier`;
}

/* Funcțiile de mai jos nu sunt componente și nu pot chema hook-uri, deci
   folosesc `t` de modul: limba e aceeași, iar cardul se redesenează oricum cu
   pagina care îl conține. */

/** Rândul de sub nume: unde e, ce regim are, cât teren. */
function descriere(project: api.Project): string {
  const r = project.rezumat;
  const bucati = [r?.adresa, r?.regim,
                  r?.teren_mp
                    ? tr("proiecte.teren", { suprafata: numar(Math.round(r.teren_mp)) })
                    : null];
  const text = bucati.filter(Boolean).join(" · ");
  return text || tr("proiecte.fara_dosar_inca");
}

function chipuri(project: api.Project) {
  const r = project.rezumat;
  const out = [];
  if (r?.job) {
    out.push(
      <Chip key="job" tone="acc">
        <span className="pulse viu" style={{ width: 6, height: 6 }} />
        {tr("proiecte.se_genereaza", { ce: r.job.eticheta })}
      </Chip>);
  }
  for (const d of r?.discipline ?? []) {
    out.push(
      <Chip key={d.key} tone="mut">
        {numeDisciplina(d.key)} v{d.versiune}
      </Chip>);
  }
  if (r?.neconforme) {
    out.push(
      <Chip key="nc" tone="bad">
        {tr("proiecte.neconforme", { n: r.neconforme })}
      </Chip>);
  }
  if (r && !r.job && r.fisiere === 0) {
    out.push(
      <span key="dosar" className="chip c-out">{tr("proiecte.fara_dosar")}</span>);
  }
  return out;
}

/** Sub titlu: câte proiecte sunt și câte lucrează chiar acum. */
function sumar(projects: api.Project[]): string {
  const bucati = [tn("proiecte.numar#n", projects.length)];
  const vii = projects.filter((p) => p.rezumat?.job).length;
  if (vii) bucati.push(tr("proiecte.ruleaza_acum", { n: vii }));
  return bucati.join(" · ");
}

/** Subsolul mono: când s-a atins ultima dată și cât dosar are. */
function subsol(project: api.Project): string {
  const cand = project.updated_at ?? project.created_at;
  const bucati = [cand ? tr("proiecte.actualizat", { cand: candText(cand) }) : null];
  const n = project.rezumat?.fisiere;
  if (n != null) bucati.push(tn("proiecte.documente#n", n));
  return bucati.filter(Boolean).join(" · ");
}

function candText(iso: string): string {
  const d = new Date(iso);
  const azi = new Date();
  const zi = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const zile = Math.round((zi(azi) - zi(d)) / 86400000);
  if (zile <= 0) return tr("proiecte.azi", { ora: ora(d) });
  if (zile === 1) return tr("proiecte.ieri");
  if (d.getFullYear() === azi.getFullYear()) {
    return data(iso, { day: "2-digit", month: "2-digit" });
  }
  return data(iso);
}
